import React, { useState, useEffect, useCallback } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Button, Badge, Spinner, Image, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Plus, Warning, Pencil, Info, UserCircle } from 'phosphor-react'
import api from '../../../services/api'
import { toast } from 'react-toastify'

import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../components/SearchInput'

interface IDriver {
  name: string
  user_id: string
  birth: Date
  city: string
  state: string
  profilePicture: { url: string }
  account_validated: boolean
}

const Motoristas: React.FC = () => {
  const [drivers, setDrivers] = useState<IDriver[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const navigate = useNavigate()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/company/drivers?driverInfo=true&profile_picture=true&limit=12&show_invalid=true'
    if (search) apiUrl += '&name=' + search

    const offset = drivers && !isSearchDifferent ? drivers.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      const driversData = data.drivers
      if (!driversData.length) {
        if (isSearchDifferent) setDrivers(data)
        setHasMoreData(false)
      } else {
        if (!drivers) setDrivers(driversData)
        else setDrivers(isSearchDifferent ? driversData : drivers.concat(driversData))
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, drivers, hasMoreData, setIsLoading])

  useEffect(() => {
    if (!drivers && hasMoreData) loadData()
  }, [loadData, hasMoreData, drivers])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Motoristas">
      <LoadingSpinner text="Buscando motoristas. Aguarde..." />
      <Row style={{ marginInline: 25 }}>
        <SearchInput
          placeholder="Digite o nome do motorista"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', justifyContent: 'right', marginInline: 30 }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={`${styles.new_driver_button} px-3 px-md-5`}
            onClick={() => navigate('/empresa/motoristas/novo')}
          >
            <Plus
              style={{ marginBottom: 4, marginRight: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Novo motorista
          </Button>
        </Col>
      </Row>
      <Row style={{ marginInline: 30, display: 'flex', justifyContent: 'left' }}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!drivers?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhum
            motorista encontrado!
          </Badge>
        )}
        {drivers &&
          drivers.length > 0 &&
          drivers?.map((driver, index) => (
            <Col key={index} style={{ padding: 8 }} className="col-12 col-md-6 col-lg-6 col-xl-4">
              <div className={styles.driver_container}>
                <Col md={5} style={{ alignSelf: 'center' }} className="col-5">
                  {driver.profilePicture ? (
                    <Image className={styles.image} fluid={false} src={driver.profilePicture.url} />
                  ) : (
                    <UserCircle size={'100%'} />
                  )}
                </Col>
                <Col md={7} className={`col-7 ${styles.driver_info}`}>
                  <Row style={{ textAlign: 'center', margin: 0 }}>
                    <span className={styles.driver_name}>{driver.name}</span>
                    <span
                      style={{
                        fontWeight: '400',
                        fontSize: 12,
                      }}
                    >
                      {driver.city} - {driver.state}
                    </span>
                    {!driver.account_validated && (
                      <div>
                        <Badge bg="danger" style={{ marginTop: 5, fontSize: 10 }}>
                          Conta não validada
                        </Badge>
                      </div>
                    )}
                  </Row>
                  <Row className={styles.btn_container}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Editar motorista</Tooltip>}
                    >
                      <div
                        className={styles.edit_btn}
                        onClick={() => navigate(`/empresa/motoristas/editar/${driver.user_id}`)}
                      >
                        <Pencil color="#053C5E" size={22} />
                      </div>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Mais informações</Tooltip>}
                    >
                      <div
                        className={styles.info_btn}
                        onClick={() => navigate(`/empresa/motoristas/info/${driver.user_id}`)}
                      >
                        <Info color="#FFFFFF" size={22} />
                      </div>
                    </OverlayTrigger>
                  </Row>
                </Col>
              </div>
            </Col>
          ))}
      </Row>
    </DashboardLayout>
  )
}

export default Motoristas
